import config from '../config'
import { get, post } from './ApiService'

export type WeeklyWorkPlanStatus = 'draft' | 'published' | 'closed'

export interface WeeklyWorkPlan {
  _id: string
  status: WeeklyWorkPlanStatus
  version: number
  denominator: number
  updatedAt?: string
  publishedAt?: string
  closedAt?: string
  retrospective?: boolean
  retrospectiveReason?: string
  retrospectiveAuthorizedBy?: string
  retrospectiveAuthorizedAt?: string
  ppcResult?: {
    numerator: number
    denominator: number
    value: number | null
  }
}

export interface WeeklyCommitment {
  _id: string
  task: string
  taskSnapshot?: {
    id?: string
    name?: string
    type?: string
  }
  performerIds?: string[]
  promisedDate?: string
  conditionsOfSatisfaction?: string[]
  eligible: boolean
  status: 'proposed' | 'committed' | 'declared_complete' | 'resolved'
  result?: 'kept' | 'not_kept' | 'withdrawn'
  completion?: {
    declaredAt?: string
    completedAt?: string
    declaredBy?: string
    comment?: string
    evidence?: any
  }
  acceptance?: {
    accepted?: boolean
    acceptedAt?: string
    acceptedBy?: string
    comment?: string
    evidence?: any
  }
  restrictionSnapshot?: {
    state?: 'ready' | 'blocked'
  }
}

export interface WeeklyWorkPlanDraftCommitment {
  taskId: string
  performerIds: string[]
  promisedDate?: string
  conditionsOfSatisfaction: string[]
}

export interface WeeklyWorkPlanPeriodResult {
  plan: WeeklyWorkPlan | null
  commitments: WeeklyCommitment[]
}

export const loadWeeklyWorkPlanPeriod = async (
  projectId: string,
  periodKey: string,
): Promise<WeeklyWorkPlanPeriodResult> => {
  const query = new URLSearchParams({
    project_id: projectId,
    period_key: periodKey,
  })
  const response = await get(
    `${config.apiHost}/weekly-work-plans?${query.toString()}`,
    'WEEKLY_WORK_PLAN_LOAD_PERIOD',
  )

  if (response.error) throw new Error(response.error)

  const payload = response.data?.data || response.data || {}
  const plans: WeeklyWorkPlan[] = payload.plans || []

  return {
    plan: plans[0] || null,
    commitments: payload.commitments || [],
  }
}

export const saveWeeklyWorkPlanDraft = async (input: {
  projectId: string
  periodKey: string
  expectedVersion?: number
  retrospectiveReason?: string
  commitments: WeeklyWorkPlanDraftCommitment[]
}): Promise<WeeklyWorkPlanPeriodResult> => {
  const response = await post(
    `${config.apiHost}/weekly-work-plans`,
    input,
    'WEEKLY_WORK_PLAN_SAVE_DRAFT',
  )

  if (response.error) throw new Error(response.error)

  const payload = response.data?.data || response.data || {}
  return {
    plan: payload.plan || null,
    commitments: payload.commitments || [],
  }
}

export const publishWeeklyWorkPlan = async (
  planId: string,
  expectedVersion: number,
  retrospectiveReason?: string,
): Promise<WeeklyWorkPlanPeriodResult> => {
  const response = await post(
    `${config.apiHost}/weekly-work-plans/${planId}/publish`,
    { expectedVersion, ...(retrospectiveReason ? { retrospectiveReason } : {}) },
    'WEEKLY_WORK_PLAN_PUBLISH',
  )

  if (response.error) throw new Error(response.error)

  const payload = response.data?.data || response.data || {}
  return {
    plan: payload.plan || null,
    commitments: payload.commitments || [],
  }
}

const unwrapCommitment = (response: any): WeeklyCommitment => {
  if (response.error) throw new Error(response.error)
  return response.data?.data || response.data || {}
}

export const declareWeeklyCommitmentComplete = async (
  commitmentId: string,
  input: { completedAt?: string, comment?: string, evidence?: any } = {},
): Promise<WeeklyCommitment> => unwrapCommitment(await post(
  `${config.apiHost}/weekly-commitments/${commitmentId}/declare-complete`,
  input,
  'WEEKLY_COMMITMENT_DECLARE_COMPLETE',
))

export const resolveWeeklyCommitment = async (
  commitmentId: string,
  input: { accepted: boolean, comment?: string, evidence?: any },
): Promise<WeeklyCommitment> => unwrapCommitment(await post(
  `${config.apiHost}/weekly-commitments/${commitmentId}/accept`,
  input,
  'WEEKLY_COMMITMENT_RESOLVE',
))

export const closeWeeklyWorkPlan = async (
  planId: string,
): Promise<WeeklyWorkPlanPeriodResult> => {
  const response = await post(
    `${config.apiHost}/weekly-work-plans/${planId}/close`,
    {},
    'WEEKLY_WORK_PLAN_CLOSE',
  )

  if (response.error) throw new Error(response.error)

  const payload = response.data?.data || response.data || {}
  return {
    plan: payload.plan || null,
    commitments: payload.commitments || [],
  }
}
